import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { FreelancerSearchResult } from "@/services/campaigns-api";

/**
 * Sugestões de destinatários para campanhas manuais, uma por "sinal" de
 * negócio. Cada sinal lê de uma golden-record view (uma linha por
 * freelancer, sql/146) que NÃO tem nome/email — por isso a busca é em 2
 * passos: pagina a view por .range() (nunca .in() com lista grande) e depois
 * busca os freelancers dessa página em lote (≤25 ids, seguro para não
 * estourar a URL/414 do nginx).
 */
export type SinalCampanha = "quase_aprovados" | "cadastro_parado" | "engajados";

export const CAMPANHA_SUGESTOES_PAGE_SIZE = 25;

export interface SugestaoQuaseAprovado extends FreelancerSearchResult {
  freelancerId: string;
  vezesPreterido: number;
  ultimaVagaEm: string | null;
}

export interface SugestaoCadastroParado extends FreelancerSearchResult {
  freelancerId: string;
  /** GREATEST(jotform, linkedin) calculado no client — ver comentário abaixo. */
  ultimaAtualizacaoPropriaEm: string | null;
}

export interface SugestaoEngajado extends FreelancerSearchResult {
  freelancerId: string;
  ultimoContatoEm: string | null;
  taxaClique: number | null;
}

export type SugestaoCampanha =
  | SugestaoQuaseAprovado
  | SugestaoCadastroParado
  | SugestaoEngajado;

interface FreelancerElegivel {
  id: string;
  nome: string | null;
  email: string | null;
  is_active: boolean | null;
}

/** Mesmo critério de elegibilidade do autocomplete manual (Campanhas.tsx):
 *  sem descadastro, ativo (ou sem flag) e com email cadastrado. Recebe no
 *  máximo uma página de sinais (25 ids) por vez, nunca a lista inteira. */
async function buscarFreelancersElegiveis(
  freelancerIds: string[],
): Promise<Map<string, FreelancerElegivel>> {
  if (freelancerIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from("freelancers")
    .select("id, nome, email, is_active, unsubscribed_at")
    .in("id", freelancerIds)
    .is("unsubscribed_at", null);
  if (error) throw error;
  const elegiveis = (data ?? []).filter(
    (freelancer) => !!freelancer.email && freelancer.is_active !== false,
  );
  return new Map(elegiveis.map((freelancer) => [freelancer.id, freelancer]));
}

// Seis meses em milissegundos, usado no filtro/corte de "cadastro parado".
const SEIS_MESES_MS = 1000 * 60 * 60 * 24 * 30 * 6;

interface QuaseAprovadoLinhaBruta {
  freelancer_id: string;
  vezes_preterido: number;
  ultima_vaga_em: string | null;
}

interface CadastroParadoLinhaBruta {
  freelancer_id: string;
  ultima_atualizacao_jotform: string | null;
  ultima_atualizacao_linkedin: string | null;
}

interface EngajadoLinhaBruta {
  freelancer_id: string;
  ultimo_contato_em: string | null;
  taxa_clique: number | null;
}

interface PaginaSugestoes<Linha> {
  linhas: Linha[];
  ultimaPaginaCheia: boolean;
}

/** GREATEST(jotform, linkedin) calculado no client: a view expõe as 4 origens
 *  separadas, mas não um agregado só de "iniciativa do próprio freelancer"
 *  (interno/IA não conta como interesse dele — ver docstring da view). */
function maiorData(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

async function buscarPaginaQuaseAprovados(pageParam: number): Promise<PaginaSugestoes<SugestaoQuaseAprovado>> {
  const inicio = pageParam;
  const fim = inicio + CAMPANHA_SUGESTOES_PAGE_SIZE - 1;
  const { data, error } = await supabase
    .from("v_freelancer_funil" as never)
    .select("freelancer_id, vezes_preterido, ultima_vaga_em")
    .gt("vezes_preterido", 0)
    .order("ultima_vaga_em", { ascending: false, nullsFirst: false })
    .range(inicio, fim);
  if (error) throw error;
  const linhasBrutas = (data as unknown as QuaseAprovadoLinhaBruta[]) ?? [];
  const freelancersMap = await buscarFreelancersElegiveis(linhasBrutas.map((linha) => linha.freelancer_id));
  const linhas = linhasBrutas
    .map((linha) => {
      const freelancer = freelancersMap.get(linha.freelancer_id);
      if (!freelancer) return null;
      const sugestao: SugestaoQuaseAprovado = {
        freelancerId: linha.freelancer_id,
        nome: freelancer.nome ?? "",
        email: freelancer.email as string,
        vezesPreterido: linha.vezes_preterido,
        ultimaVagaEm: linha.ultima_vaga_em,
      };
      return sugestao;
    })
    .filter((linha): linha is SugestaoQuaseAprovado => linha !== null);
  return { linhas, ultimaPaginaCheia: linhasBrutas.length === CAMPANHA_SUGESTOES_PAGE_SIZE };
}

async function buscarPaginaCadastroParado(pageParam: number): Promise<PaginaSugestoes<SugestaoCadastroParado>> {
  const cortePadrao = new Date(Date.now() - SEIS_MESES_MS).toISOString();
  const inicio = pageParam;
  const fim = inicio + CAMPANHA_SUGESTOES_PAGE_SIZE - 1;
  const { data, error } = await supabase
    .from("v_freelancer_perfil_frescor" as never)
    .select("freelancer_id, ultima_atualizacao_jotform, ultima_atualizacao_linkedin")
    // GREATEST(jotform, linkedin) < corte, tratando NULL como "nunca atualizou"
    // (equivalente a AND das duas origens: cada uma nula OU anterior ao corte).
    .or(
      `and(ultima_atualizacao_jotform.is.null,ultima_atualizacao_jotform.lt.${cortePadrao}),and(ultima_atualizacao_jotform.is.null,ultima_atualizacao_linkedin.is.null),and(ultima_atualizacao_jotform.lt.${cortePadrao},ultima_atualizacao_linkedin.is.null),and(ultima_atualizacao_jotform.lt.${cortePadrao},ultima_atualizacao_linkedin.lt.${cortePadrao})`,
    )
    // PostgREST não ordena por expressão (sem GREATEST no order); usamos as
    // colunas reais como chave de paginação estável e reordenamos cada
    // página no client pelo valor combinado (ver maiorData abaixo).
    .order("ultima_atualizacao_jotform", { ascending: true, nullsFirst: true })
    .order("ultima_atualizacao_linkedin", { ascending: true, nullsFirst: true })
    .range(inicio, fim);
  if (error) throw error;
  const linhasBrutas = (data as unknown as CadastroParadoLinhaBruta[]) ?? [];
  const freelancersMap = await buscarFreelancersElegiveis(linhasBrutas.map((linha) => linha.freelancer_id));
  const linhas = linhasBrutas
    .map((linha) => {
      const freelancer = freelancersMap.get(linha.freelancer_id);
      if (!freelancer) return null;
      const sugestao: SugestaoCadastroParado = {
        freelancerId: linha.freelancer_id,
        nome: freelancer.nome ?? "",
        email: freelancer.email as string,
        ultimaAtualizacaoPropriaEm: maiorData(
          linha.ultima_atualizacao_jotform,
          linha.ultima_atualizacao_linkedin,
        ),
      };
      return sugestao;
    })
    .filter((linha): linha is SugestaoCadastroParado => linha !== null)
    // Reordena a página pelo valor combinado (mais parado primeiro / nulls primeiro).
    .sort((a, b) => {
      if (!a.ultimaAtualizacaoPropriaEm && !b.ultimaAtualizacaoPropriaEm) return 0;
      if (!a.ultimaAtualizacaoPropriaEm) return -1;
      if (!b.ultimaAtualizacaoPropriaEm) return 1;
      return a.ultimaAtualizacaoPropriaEm < b.ultimaAtualizacaoPropriaEm ? -1 : 1;
    });
  return { linhas, ultimaPaginaCheia: linhasBrutas.length === CAMPANHA_SUGESTOES_PAGE_SIZE };
}

async function buscarPaginaEngajados(pageParam: number): Promise<PaginaSugestoes<SugestaoEngajado>> {
  const inicio = pageParam;
  const fim = inicio + CAMPANHA_SUGESTOES_PAGE_SIZE - 1;
  const { data, error } = await supabase
    .from("v_freelancer_engajamento" as never)
    .select("freelancer_id, ultimo_contato_em, taxa_clique")
    .not("ultimo_contato_em", "is", null)
    .order("ultimo_contato_em", { ascending: false })
    .range(inicio, fim);
  if (error) throw error;
  const linhasBrutas = (data as unknown as EngajadoLinhaBruta[]) ?? [];
  const freelancersMap = await buscarFreelancersElegiveis(linhasBrutas.map((linha) => linha.freelancer_id));
  const linhas = linhasBrutas
    .map((linha) => {
      const freelancer = freelancersMap.get(linha.freelancer_id);
      if (!freelancer) return null;
      const sugestao: SugestaoEngajado = {
        freelancerId: linha.freelancer_id,
        nome: freelancer.nome ?? "",
        email: freelancer.email as string,
        ultimoContatoEm: linha.ultimo_contato_em,
        taxaClique: linha.taxa_clique,
      };
      return sugestao;
    })
    .filter((linha): linha is SugestaoEngajado => linha !== null);
  return { linhas, ultimaPaginaCheia: linhasBrutas.length === CAMPANHA_SUGESTOES_PAGE_SIZE };
}

function buscarPaginaPorSinal(
  sinal: SinalCampanha,
  pageParam: number,
): Promise<PaginaSugestoes<SugestaoCampanha>> {
  if (sinal === "quase_aprovados") return buscarPaginaQuaseAprovados(pageParam);
  if (sinal === "cadastro_parado") return buscarPaginaCadastroParado(pageParam);
  return buscarPaginaEngajados(pageParam);
}

/**
 * Sugestões de destinatários por sinal de negócio ("Quase aprovados",
 * "Cadastro parado", "Engajados"), paginadas de 25 em 25 via .range() na
 * golden-record view correspondente (sql/146), com "Mostrar mais" client-side.
 */
export function useCampanhaSugestoes(sinal: SinalCampanha, habilitado: boolean) {
  return useInfiniteQuery({
    queryKey: ["campanha-sugestoes", sinal],
    enabled: habilitado,
    staleTime: 60_000,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => buscarPaginaPorSinal(sinal, pageParam),
    getNextPageParam: (ultimaPagina, todasPaginas) => {
      if (!ultimaPagina.ultimaPaginaCheia) return undefined;
      return todasPaginas.length * CAMPANHA_SUGESTOES_PAGE_SIZE;
    },
  });
}
